// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import {
    ProductPartList,
    QuickProductLookupBaseModel,
    QuickProductLookupModel,
} from "@stoneage/App/Services/QuickProductLookup/QuickProductLookupService";
import { Draft } from "immer";

type QuickProductLookupState = Readonly<{
    isLoading: boolean;
    products: QuickProductLookupModel | null;
    filteredProducts: QuickProductLookupBaseModel[] | undefined;
    paginatedProducts: QuickProductLookupBaseModel[] | undefined;
    productPartList: { [key: string]: ProductPartList[] };
    pagination: PaginationModel;
    isLoadingPartList: { [key: string]: boolean };
}>;

const initialProductLookupState: QuickProductLookupState = {
    isLoading: true,
    products: null,
    filteredProducts: undefined,
    paginatedProducts: undefined,
    productPartList: {},
    isLoadingPartList: {},
    pagination: {} as PaginationModel,
};

const reducer = {
    "Pages/ProductLookup/BeginLoadingProducts": (draft: Draft<QuickProductLookupState>) => {
        draft.isLoading = true;
    },
    "Pages/ProductLookup/CompletedLoadingProducts": (
        draft: Draft<QuickProductLookupState>,
        actions: {
            products: QuickProductLookupModel | null;
            filteredProducts: QuickProductLookupBaseModel[] | undefined;
            paginatedProducts: QuickProductLookupBaseModel[] | undefined;
            pagination: PaginationModel;
        },
    ) => {
        draft.isLoading = false;
        draft.products = actions.products;
        draft.filteredProducts = actions.filteredProducts;
        draft.paginatedProducts = actions.paginatedProducts;
        draft.pagination = actions.pagination;
    },
    "Pages/ProductLookup/SetFilteredProducts": (
        draft: Draft<QuickProductLookupState>,
        actions: {
            filteredProducts: QuickProductLookupBaseModel[] | undefined;
            paginatedProducts: QuickProductLookupBaseModel[] | undefined;
            pagination: PaginationModel;
        },
    ) => {
        draft.filteredProducts = actions.filteredProducts;
        draft.paginatedProducts = actions.paginatedProducts;
        draft.pagination = actions.pagination;
    },
    "Pages/ProductLookup/SetPaginatedProducts": (
        draft: Draft<QuickProductLookupState>,
        actions: {
            paginatedProducts: QuickProductLookupBaseModel[] | undefined;
            pagination: PaginationModel;
        },
    ) => {
        draft.paginatedProducts = actions.paginatedProducts;
        draft.pagination = actions.pagination;
    },
    "Pages/ProductLookip/SetIsLoadingProductPartList": (
        draft: Draft<QuickProductLookupState>,
        actions: { productNumber: string },
    ) => {
        draft.isLoadingPartList[actions.productNumber] = true;
    },
    "Pages/ProductLookup/SetProductPartList": (
        draft: Draft<QuickProductLookupState>,
        actions: { productPartList: { [key: string]: ProductPartList[] } },
    ) => {
        draft.isLoadingPartList = {};
        draft.productPartList = actions.productPartList;
    },
};

const xProductLookup = createTypedReducerWithImmer(initialProductLookupState, reducer);

(reducers as any).xProductLookup = xProductLookup;

export type xProductLookupState = {
    xProductLookup: QuickProductLookupState;
};

type xProductlookupReducers = {
    isLoading: typeof xProductLookup;
    data: typeof xProductLookup;
};

export type xProductLookupActions = Parameters<xProductlookupReducers[keyof xProductlookupReducers]>[1] | AnyAction;
